import { Injectable, NgZone } from '@angular/core';

/** Retângulos no referencial do viewport, em pixels CSS. */
export interface GenieRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface GenieAnimation {
  /** Resolve mesmo quando a animação é cancelada. */
  finished: Promise<void>;
  /** Remove a animação e repõe o estilo anterior. Pode ser chamado várias vezes. */
  cancel: () => void;
}

const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v));
const lerp = (a: number, b: number, p: number): number => a + (b - a) * p;

/**
 * Efeito de sucção leve, sem html2canvas, snapshots ou cópias do DOM.
 *
 * Anima a própria janela com Web Animations API: transform + opacity.
 * O Angular CDK gere o transform fora da animação; os seus estilos originais
 * regressam ao terminar/cancelar. O componente Angular não é recriado.
 *
 * NOTA: a deformação é uma aproximação ao Genie do macOS, não um mesh 2D.
 */
@Injectable({ providedIn: 'root' })
export class ISDialogGenieAnimationService {
  constructor(private zone: NgZone) {}

  get reducedMotion(): boolean {
    return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  play(element: HTMLElement, source: GenieRect, dock: GenieRect,
       direction: 'minimize' | 'restore'): GenieAnimation {
    const noop: GenieAnimation = { finished: Promise.resolve(), cancel: () => {} };
    if (this.reducedMotion || !element.animate ||
        source.width <= 0 || source.height <= 0 ||
        dock.width <= 0 || dock.height <= 0) return noop;

    const dx = dock.left - source.left;
    const dy = dock.top - source.top;
    const scaleX = clamp(dock.width / source.width, 0.04, 1);
    const scaleY = clamp(dock.height / source.height, 0.035, 1);
    const styles = getComputedStyle(element);
    const baseTransform = styles.transform === 'none' ? 'translate3d(0px,0px,0px)' : styles.transform;
    const parsedOpacity = Number(styles.opacity);
    const opacity = Number.isFinite(parsedOpacity) ? clamp(parsedOpacity, 0, 1) : 1;
    const beforeWillChange = element.style.willChange;

    // A origem deve ser o canto superior esquerdo para que (dx,dy) leve
    // precisamente o retângulo original até ao item correspondente na taskbar.
    const effectTransform = (p: number, sx: number, sy: number, skew: number): string =>
      `${baseTransform} translate3d(${(dx * p).toFixed(2)}px, ${(dy * p).toFixed(2)}px, 0px) ` +
      `scale(${sx.toFixed(4)}, ${sy.toFixed(4)}) skewX(${skew.toFixed(2)}deg)`;

    const sign = dx < 0 ? -1 : 1;
    // Só animamos propriedades geralmente compostas pela GPU.
    const minimizeFrames: Keyframe[] = [
      { offset: 0, transformOrigin: 'top left', transform: baseTransform, opacity },
      { offset: 0.38, transformOrigin: 'top left',
        transform: effectTransform(0.2, lerp(1, scaleX, 0.16), lerp(1, scaleY, 0.14), sign * 3.5), opacity },
      { offset: 0.74, transformOrigin: 'top left',
        transform: effectTransform(0.70, lerp(1, scaleX, 0.67), lerp(1, scaleY, 0.73), sign * 2), opacity: opacity * 0.82 },
      { offset: 1, transformOrigin: 'top left',
        transform: effectTransform(1, scaleX, scaleY, 0), opacity: 0 }
    ];
    // Restaurar usa os mesmos quadros no sentido inverso, sem capturar nada.
    const frames = direction === 'minimize' ? minimizeFrames :
      minimizeFrames.slice().reverse().map((frame, index) => ({
        ...frame, offset: [0, 0.26, 0.62, 1][index]
      }));

    let animation: Animation;
    try {
      element.style.willChange = 'transform, opacity';
      animation = this.zone.runOutsideAngular(() => element.animate(frames, {
        duration: direction === 'minimize' ? 315 : 270,
        easing: 'cubic-bezier(0.25, 0.8, 0.25, 1)',
        fill: 'both'
      }));
    } catch {
      element.style.willChange = beforeWillChange;
      return noop;
    }

    let settled = false;
    let cleaned = false;
    let resolve!: () => void;
    const finished = new Promise<void>(res => { resolve = res; });
    const settle = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      animation.cancel();
      element.style.willChange = beforeWillChange;
      settle();
    };

    // Não removemos o efeito em 'finish': o último quadro deve permanecer
    // enquanto o componente assinala [hidden] (minimize) ou termina o restore.
    // Isso evita um flash de tamanho original antes de a taskbar assumir controlo.
    animation.addEventListener('finish', settle, { once: true });
    animation.addEventListener('cancel', () => {
      if (!cleaned) {
        cleaned = true;
        element.style.willChange = beforeWillChange;
      }
      settle();
    }, { once: true });

    return { finished, cancel: cleanup };
  }
}
